import React, {useEffect, useMemo, useState} from 'react';
import styles from '../../../../components/StackedBarChart/StackedBarChart.module.css';
import useApi from '../../../../hooks/useApi.hook';
import {
	getDocumentDeadlinesTable,
	DEADLINE_STATUS_LABELS
} from '../../../../helpers/constants';
import useTemplateRegistry from '../../../../hooks/useTemplateRegistry.hook';
import {
	BarChart,
	Bar,
	Cell,
	XAxis,
	YAxis,
	CartesianGrid,
	Legend,
	ResponsiveContainer
} from 'recharts';
import ErrorMessage from '../../../../components/ErrorMessage/ErrorMessage';
import Spinner from '../../../../components/Spinner/Spinner';
import Button from '../../../../components/Button/Button';
import {
	LEGEND_STYLE,
	AXIS_TICK_STYLE,
	Y_AXIS_WIDTH,
	BAR_SIZE,
	CHART_MARGIN,
	calcChartHeight,
	createStackedBarShape,
	CustomYAxisTick,
	toPercent,
	fillOpacityFor
} from '../../../../components/StackedBarChart/StackedBarChartUtils';

const STATUS_COLORS = {
	ok: '#22c55e',
	warning: '#f59e0b',
	overdue: '#ef4444',
	no_date: '#94a3b8'
};

const STACK_SEGMENT_KEYS = ['ok', 'warning', 'overdue', 'no_date'];

export function DeadlinesChart({onGoToTable}) {
	const api = useApi();
	const {byCode: templatesByCode} = useTemplateRegistry();
	const [rows, setRows] = useState([]);
	const [selectedTemplateId, setSelectedTemplateId] = useState(undefined);
	const [hoveredTemplateId, setHoveredTemplateId] = useState(undefined);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(undefined);
	const data = useMemo(
		() => rows
			.filter(row => row.total_users > 0)
			.map(row => {
				const noDate = row.breakdown?.no_date ?? 0;
				const total = row.total_users ?? 0;
				return {
					templateId: row.template_id,
					name: templatesByCode[row.template_name]?.title || row.template_name,
					total,
					ok: row.breakdown?.ok ?? 0,
					warning: row.breakdown?.warning ?? 0,
					overdue: row.breakdown?.overdue ?? 0,
					no_date: noDate,
					issued: Math.max(total - noDate, 0)
				};
			})
			.sort((a, b) => {
				if (b.overdue !== a.overdue) {
					return b.overdue - a.overdue;
				}
				if (b.warning !== a.warning) {
					return b.warning - a.warning;
				}
				return a.name.localeCompare(b.name, 'ru');
			}),
		[rows, templatesByCode]
	);
	const selectedDocument = useMemo(
		() => data.find(item => item.templateId === selectedTemplateId) || data[0] || null,
		[data, selectedTemplateId]
	);
	const activeTemplateId = selectedDocument?.templateId;

	useEffect(() => {
		const fetchData = async () => {
			setLoading(true);
			try {
				const response = await api.get(getDocumentDeadlinesTable);
				setRows(response.data);
			} catch (e) {
				setError(e.response?.data?.detail || e.response?.data?.message || 'Ошибка загрузки данных');
			} finally {
				setLoading(false);
			}
		};
		fetchData();
	}, []);

	if (loading) return <Spinner />;
	if (error) return <ErrorMessage error={error} onClose={() => setError(undefined)} />;
	if (data.length === 0) return <div className={styles.message}>Нет данных для отображения</div>;
	if (!selectedDocument) return <div className={styles.message}>Нет данных для отображения</div>;

	const chartHeight = calcChartHeight(data.length);
	const activeIndex = data.findIndex(item => item.templateId === activeTemplateId);
	const hoveredIndex = hoveredTemplateId == null
		? undefined
		: data.findIndex(item => item.templateId === hoveredTemplateId);
	const hoverRow = (index) => {
		setHoveredTemplateId(index == null ? undefined : data[index]?.templateId);
	};
	const selectRow = (index) => {
		const templateId = data[index]?.templateId;
		if (templateId) {
			setSelectedTemplateId(templateId);
		}
	};

	return (
		<div className={styles.wrapper}>
			<div className={styles.chartColumn}>
				<div className={styles.header}>
					<h3 className={styles.title}>Статистика по срокам документов</h3>
					<span className={styles.subtitle}>Клик по строке открывает детали</span>
				</div>
				<div className={styles.chartCanvas} style={{height: `${chartHeight}px`}}>
					<ResponsiveContainer width="100%" height="100%" debounce={50}>
						<BarChart
							data={data}
							layout="vertical"
							margin={CHART_MARGIN}
							barSize={BAR_SIZE}
							barCategoryGap={8}
							onMouseMove={(state, event) => {
								if (event?.target?.closest?.('.chart-row-tick')) {
									return;
								}
								const hovered = state?.activePayload?.[0]?.payload;
								setHoveredTemplateId(hovered?.templateId);
							}}
							onMouseLeave={() => setHoveredTemplateId(undefined)}
							onClick={(state) => {
								const clicked = state?.activePayload?.[0]?.payload;
								if (clicked?.templateId) {
									setSelectedTemplateId(clicked.templateId);
								}
							}}
						>
							<CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
							<XAxis
								type="number"
								allowDecimals={false}
								tick={AXIS_TICK_STYLE}
							/>
							<YAxis
								type="category"
								dataKey="name"
								tick={(tickProps) => (
									<CustomYAxisTick
										{...tickProps}
										activeIndex={activeIndex}
										hoveredIndex={hoveredIndex}
										onHoverIndex={hoverRow}
										onSelectIndex={selectRow}
									/>
								)}
								width={Y_AXIS_WIDTH}
								interval={0}
							/>
							<Legend
								formatter={(value) => DEADLINE_STATUS_LABELS[value]}
								wrapperStyle={LEGEND_STYLE}
								verticalAlign="top"
							/>
							<Bar
								dataKey="ok"
								stackId="a"
								fill={STATUS_COLORS.ok}
								name="ok"
								shape={createStackedBarShape('ok', STACK_SEGMENT_KEYS)}
							>
								{data.map((entry) => (
									<Cell
										key={`ok-${entry.templateId}`}
										fill={STATUS_COLORS.ok}
										fillOpacity={fillOpacityFor(entry.templateId, activeTemplateId, hoveredTemplateId)}
										stroke={entry.templateId === activeTemplateId ? '#15803d' : 'none'}
										strokeWidth={entry.templateId === activeTemplateId ? 0.8 : 0}
									/>
								))}
							</Bar>
							<Bar
								dataKey="warning"
								stackId="a"
								fill={STATUS_COLORS.warning}
								name="warning"
								shape={createStackedBarShape('warning', STACK_SEGMENT_KEYS)}
							>
								{data.map((entry) => (
									<Cell
										key={`warning-${entry.templateId}`}
										fill={STATUS_COLORS.warning}
										fillOpacity={fillOpacityFor(entry.templateId, activeTemplateId, hoveredTemplateId)}
										stroke={entry.templateId === activeTemplateId ? '#b45309' : 'none'}
										strokeWidth={entry.templateId === activeTemplateId ? 0.8 : 0}
									/>
								))}
							</Bar>
							<Bar
								dataKey="overdue"
								stackId="a"
								fill={STATUS_COLORS.overdue}
								name="overdue"
								shape={createStackedBarShape('overdue', STACK_SEGMENT_KEYS)}
							>
								{data.map((entry) => (
									<Cell
										key={`overdue-${entry.templateId}`}
										fill={STATUS_COLORS.overdue}
										fillOpacity={fillOpacityFor(entry.templateId, activeTemplateId, hoveredTemplateId)}
										stroke={entry.templateId === activeTemplateId ? '#b91c1c' : 'none'}
										strokeWidth={entry.templateId === activeTemplateId ? 0.8 : 0}
									/>
								))}
							</Bar>
							<Bar
								dataKey="no_date"
								stackId="a"
								fill="transparent"
								stroke={STATUS_COLORS.no_date}
								strokeWidth={1.2}
								name="no_date"
								shape={createStackedBarShape('no_date', STACK_SEGMENT_KEYS, {outline: true})}
							>
								{data.map((entry) => {
									const isSelected = entry.templateId === activeTemplateId;
									const hasHover = hoveredTemplateId !== undefined;
									const isHovered = entry.templateId === hoveredTemplateId;
									const strokeOpacity = hasHover ? (isHovered ? 0.8 : 0.35) : (isSelected ? 0.8 : 0.55);
									return (
										<Cell
											key={`no-date-${entry.templateId}`}
											fill="transparent"
											stroke={STATUS_COLORS.no_date}
											strokeOpacity={strokeOpacity}
											strokeWidth={isSelected ? 1.4 : 1.2}
										/>
									);
								})}
							</Bar>
						</BarChart>
					</ResponsiveContainer>
				</div>
			</div>
			<aside className={styles.detailsCard}>
				<div className={styles.detailsHeader}>
					<div className={styles.detailsTitle}>{selectedDocument.name}</div>
					{onGoToTable && (
						<Button
							className={styles.toTableButton}
							onClick={() => onGoToTable(selectedDocument.templateId)}
						>
							В таблицу
						</Button>
					)}
				</div>
				<div className={styles.metrics}>
					<div className={styles.metricRow}>
						<span className={styles.metricLabel}>Назначено сотрудников</span>
						<span className={styles.metricValue}>{selectedDocument.total}</span>
					</div>
					<div className={styles.metricRow}>
						<span className={styles.metricLabel}>Оформлено документов</span>
						<span className={styles.metricValue}>
							{selectedDocument.issued} ({toPercent(selectedDocument.issued, selectedDocument.total)})
						</span>
					</div>
					<div className={styles.metricRow}>
						<span className={styles.metricLabel}>Сроки в норме</span>
						<span className={styles.metricValueOk}>
							{selectedDocument.ok} ({toPercent(selectedDocument.ok, selectedDocument.total)})
						</span>
					</div>
					<div className={styles.metricRow}>
						<span className={styles.metricLabel}>Скоро истекают</span>
						<span className={styles.metricValueWarning}>
							{selectedDocument.warning} ({toPercent(selectedDocument.warning, selectedDocument.total)})
						</span>
					</div>
					<div className={styles.metricRow}>
						<span className={styles.metricLabel}>Просрочены</span>
						<span className={styles.metricValueOverdue}>
							{selectedDocument.overdue} ({toPercent(selectedDocument.overdue, selectedDocument.total)})
						</span>
					</div>
					<div className={styles.metricRow}>
						<span className={styles.metricLabel}>Нет данных</span>
						<span className={styles.metricValueNoData}>
							{selectedDocument.no_date} ({toPercent(selectedDocument.no_date, selectedDocument.total)})
						</span>
					</div>
				</div>
			</aside>
		</div>
	);
}
